import Alert, { AlertError } from 'components/alert/Alert';
import NavBackButton from 'components/buttons/NavBackButton';
import { Headline4, BodyLarge, FlexMd, FlexColXs } from 'components/Elements';
import useNeveForm from 'components/forms/NeveForm';
import { ErrorNotifier } from 'components/notifiers/ErrorNotifier';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import useDeleteRequest from 'hooks/useDeleteRequest';
import useModifyRequest from 'hooks/useModifyRequest';
import { DOCUMENTS_URL } from 'lib/_api-helpers';
import React from 'react';
import { breakpoints, sizes, theme } from 'theme';
import { GenerateTypeContentComponent } from '../GenerateTypeContentComponent';
import strings from '../../../../../strings/strings.json';
import Hint from 'components/Hint';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from 'shared/error-messages';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import {
    ReviewGeneratedContentProps,
    Container,
    ContentCard,
    ApproveFormCard,
    ErrorRow,
} from './ReviewGeneratedSharedComponents';
import styled from 'styled-components';
import TextField from 'components/forms/TextField';
import { QuestionCard } from '../../activityForms/shared/SharedQuestionnaireComponents';
import Button from 'components/buttons/Button';

export default function ReviewGeneratedModuleQuestionnaire({
    item,
    backButton,
}: ReviewGeneratedContentProps): JSX.Element {
    const context = React.useContext(BuildCourseContext);
    const [showError, setShowError] = React.useState<boolean>();
    const [openRejectAlert, setOpenRejectAlert] = React.useState(false);
    const [alertError, setAlertError] = React.useState<AlertError>();
    const [getQuestionnaireError, setGetQuestionnaireError] = React.useState<boolean>(false);

    const {
        register,
        unregister,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useNeveForm<GeneratedQuestionnaire>({
        questions: [],
    });
    const questions = watch('questions');

    React.useEffect(() => {
        const getQuestionnaire = async () => {
            try {
                const response = await fetch(`${DOCUMENTS_URL}/${item.textContent}`);
                if (!response.ok) {
                    setGetQuestionnaireError(true);
                }
                const data = await response.json();
                if (!isGeneratedQuestionnaire(data)) {
                    setGetQuestionnaireError(true);
                }
                const questionnaire = data as GeneratedQuestionnaire;
                setValue('questions', questionnaire.questions);
            } catch (error) {
                setGetQuestionnaireError(true);
            }
        };

        getQuestionnaire();
    }, [item.textContent]); // eslint-disable-line

    const { modifyData: approveQuestionnaire, loading: approving } = useModifyRequest(
        `activities/task/${item.activityId}/approve-questionnaire`,
        'PUT',
    );

    const { deleteData: rejectStatus, deleteInProgress: rejecting } = useDeleteRequest(
        `generate-content-statuses/${item.id}`,
    );

    const approve = async (formData: GeneratedQuestionnaire) => {
        const { errors } = await approveQuestionnaire({
            questions: formData.questions,
        });
        if (errors && errors.length) {
            setShowError(true);
        } else {
            await context.refetchCourse();
            backButton();
        }
    };

    const reject = async () => {
        const { errors } = await rejectStatus();
        if (errors && errors.length) {
            const error: AlertError = {
                title: strings.courseAIGeneratedContentComponent.rejectAlert.alertErrorTitle,
                description: strings.courseAIGeneratedContentComponent.rejectAlert.alertErrorDescription,
            };
            setAlertError(error);
        } else {
            await context.refetchCourse();
        }
    };

    const removeQuestion = (questionIndex: number) => {
        const newQuestions = questions.filter((q, index) => index !== questionIndex);
        unregister(`questions.${questionIndex}`);
        setValue('questions', newQuestions);
    };

    if (getQuestionnaireError) {
        <Container>
            <NavBackButton
                backButtonTitle={''}
                backButtonAria={strings.courseAIGeneratedContentComponent.backButtonAria}
                backButtonHref={''}
                buttonFunctionOverride={backButton}
            />
            <GenerateTypeContentComponent generateType={item.generateType} />
            <Headline4>{`${strings.courseAIGeneratedContentComponent.questionnairePrefix} ${item.activityOrder}: ${item.activityTitle}`}</Headline4>
            <BodyLarge>{strings.courseAIGeneratedContentComponent.contentTitle}</BodyLarge>
            <ErrorNotifier
                title={strings.courseAIGeneratedContentComponent.reviewGeneratedTextComponent.error.title}
                description={strings.courseAIGeneratedContentComponent.reviewGeneratedTextComponent.error.description}
            />
        </Container>;
    }

    return (
        <>
            {openRejectAlert && (
                <Alert
                    buttonText={strings.courseAIGeneratedContentComponent.rejectAlert.alertButton}
                    ariaButtonText={strings.courseAIGeneratedContentComponent.rejectAlert.alertButtonAria}
                    alertText={strings.courseAIGeneratedContentComponent.rejectAlert.alertText}
                    closeAlert={() => setOpenRejectAlert(false)}
                    onSubmit={reject}
                    submitting={rejecting}
                    error={alertError}
                />
            )}
            <Container>
                <NavBackButton
                    backButtonTitle={''}
                    backButtonAria={strings.courseAIGeneratedContentComponent.backButtonAria}
                    backButtonHref={''}
                    buttonFunctionOverride={backButton}
                />
                <GenerateTypeContentComponent generateType={item.generateType} />

                <Headline4>{`${strings.courseAIGeneratedContentComponent.questionnairePrefix} ${item.activityOrder}: ${item.activityTitle}`}</Headline4>
                <ContentCard>
                    <Hint hint={strings.courseAIGeneratedContentComponent.questionnaireHint} />
                    <ApproveQuestionnaireForm onSubmit={handleSubmit(approve)}>
                        <FlexColXs>
                            {questions.map((q, i) => {
                                let questionErrorMessage = '';
                                let helpTextErrorMessage = '';

                                if (errors.questions && errors.questions[i] !== undefined) {
                                    questionErrorMessage = errors.questions[i]?.question?.message ?? '';
                                    helpTextErrorMessage = errors.questions[i]?.hint?.message ?? '';
                                }

                                return (
                                    <React.Fragment key={i}>
                                        <QuestionCard>
                                            <TextField
                                                fieldName={`question-${i}`}
                                                labelText={strings.questionnaireForm.questionInputLabel}
                                                inputAria={strings.questionnaireForm.questionInputAria}
                                                errorMessage={questionErrorMessage}
                                                required
                                                {...register(`questions.${i}.question`, {
                                                    required: {
                                                        value: true,
                                                        message: requiredFieldErrorMessage(
                                                            strings.questionnaireForm.questionInputLabel,
                                                        ),
                                                    },
                                                    maxLength: {
                                                        value: 120,
                                                        message: createErrorMessage(
                                                            strings.questionnaireForm.questionInputLabel,
                                                            fieldErrorCodes.maxLength,
                                                        ),
                                                    },
                                                })}
                                            />
                                            <TextField
                                                fieldName={`help-text-${i}`}
                                                labelText={strings.questionnaireForm.hintInputLabel}
                                                inputAria={strings.questionnaireForm.hintInputAria}
                                                errorMessage={helpTextErrorMessage}
                                                {...register(`questions.${i}.hint`, {
                                                    maxLength: {
                                                        value: 120,
                                                        message: createErrorMessage(
                                                            strings.questionnaireForm.hintInputLabel,
                                                            fieldErrorCodes.maxLength,
                                                        ),
                                                    },
                                                })}
                                            />
                                            <Button
                                                title={strings.questionnaireForm.removeQuestionButton}
                                                aria={strings.questionnaireForm.removeQuestionButtonAria}
                                                colour={'BLACK'}
                                                onClick={() => removeQuestion(i)}
                                            />
                                        </QuestionCard>
                                        <QuestionDivider />
                                    </React.Fragment>
                                );
                            })}
                        </FlexColXs>
                        <ButtonsRow>
                            <Button
                                title={strings.courseAIGeneratedContentComponent.rejectButton.title}
                                aria={strings.courseAIGeneratedContentComponent.rejectButton.aria}
                                onClick={() => setOpenRejectAlert(true)}
                                colour={'BLACK'}
                            />
                            <Button
                                title={strings.courseAIGeneratedContentComponent.approveQuestionnaireButton.title}
                                aria={strings.courseAIGeneratedContentComponent.approveQuestionnaireButton.aria}
                                type="submit"
                                submitting={approving}
                            />
                        </ButtonsRow>
                    </ApproveQuestionnaireForm>
                </ContentCard>

                {showError && (
                    <ErrorRow>
                        <ErrorNotifierSlim description={strings.courseAIGeneratedContentComponent.approveError} />
                    </ErrorRow>
                )}
            </Container>
        </>
    );
}

const ApproveQuestionnaireForm = styled(ApproveFormCard)`
    padding: unset;
`;

const QuestionDivider = styled.hr`
    border-top: 1px solid ${theme.cardSecondaryBorder};
    margin-top: ${sizes.spacingMd};
    margin-bottom: ${sizes.spacingMd};
`;

const ButtonsRow = styled(FlexMd)`
    justify-content: end;
    width: 100%;

    @media (max-width: ${breakpoints.md}) {
        flex-direction: column;
    }
`;

type Question = {
    question: string;
    hint: string;
};

type GeneratedQuestionnaire = {
    questions: Question[];
};

function isGeneratedQuestionnaire(obj: any): obj is GeneratedQuestionnaire {
    return (
        obj &&
        typeof obj === 'object' &&
        Array.isArray(obj.questions) &&
        obj.questions.every(
            (item: any) =>
                item && typeof item === 'object' && typeof item.question === 'string' && typeof item.hint === 'string',
        )
    );
}
